import { warehouseError } from './warehouseBrowse'

export const operationDefinitions = Object.freeze({
  opening: {
    title: 'Ввод остатков',
    description: 'Зафиксируйте начальное количество и стоимость товара на выбранном складе.',
    event: 'onCreateOpeningTransfer',
    requestPrefix: 'stock-opening-create',
    formTestId: 'warehouse-opening-form',
    submitTestId: 'warehouse-opening-submit',
    failure: 'Не удалось провести ввод остатков',
    success: 'Ввод остатков проведен'
  },
  transfer: {
    title: 'Перемещение',
    description: 'Переместите товар между складами без изменения общей стоимости запасов.',
    event: 'onCreateWarehouseTransfer',
    requestPrefix: 'stock-transfer-create',
    formTestId: 'warehouse-transfer-form',
    submitTestId: 'warehouse-transfer-submit',
    failure: 'Не удалось провести перемещение',
    success: 'Перемещение проведено'
  },
  adjustment: {
    title: 'Инвентаризация',
    description: 'Укажите фактическое количество; расхождение и проводки появятся после проведения.',
    event: 'onCreateInventoryAdjustment',
    requestPrefix: 'stock-adjustment-create',
    formTestId: 'warehouse-adjustment-form',
    submitTestId: 'warehouse-adjustment-submit',
    failure: 'Не удалось провести корректировку',
    success: 'Корректировка проведена'
  },
  writeOff: {
    title: 'Списание',
    description: 'Спишите запас с выбранного склада. Доступность количества проверяется при проведении.',
    event: 'onCreateStockWriteOff',
    requestPrefix: 'stock-writeoff-create',
    formTestId: 'warehouse-writeoff-form',
    submitTestId: 'warehouse-writeoff-submit',
    failure: 'Не удалось провести списание',
    success: 'Списание проведено'
  }
})

export const defaultWarehouseSettings = Object.freeze({
  default_warehouse_id: null,
  default_inventory_account_id: null,
  default_expense_account_id: null,
  show_archived_warehouses: false,
  show_archived_nomenclature: false,
  dashboard_low_stock_enabled: true,
  dashboard_diagnostics_enabled: true
})

export function todayIsoDate() {
  return new Date().toISOString().slice(0, 10)
}

export function monthStartIsoDate() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
}

export function emptyOperationForm(kind) {
  const common = { date: todayIsoDate(), nomenclature_id: null }
  if (kind === 'opening') return { ...common, warehouse_id: null, count: '', amount: '', debit_account_id: null, credit_account_id: null, comment: '' }
  if (kind === 'transfer') return { ...common, source_warehouse_id: null, destination_warehouse_id: null, count: '', inventory_account_id: null, comment: '' }
  if (kind === 'adjustment') return { ...common, warehouse_id: null, target_quantity: '', price: '', inventory_account_id: null, reason: '' }
  return { ...common, warehouse_id: null, count: '', inventory_account_id: null, expense_account_id: null, reason: '' }
}

export function normalizeWarehouseSettings(response = {}) {
  return { ...defaultWarehouseSettings, ...(response.settings || {}) }
}

export function warehouseSettingsPayload(form) {
  return {
    default_warehouse_id: form.default_warehouse_id || null,
    default_inventory_account_id: form.default_inventory_account_id || null,
    default_expense_account_id: form.default_expense_account_id || null,
    show_archived_warehouses: Boolean(form.show_archived_warehouses),
    show_archived_nomenclature: Boolean(form.show_archived_nomenclature),
    dashboard_low_stock_enabled: Boolean(form.dashboard_low_stock_enabled),
    dashboard_diagnostics_enabled: Boolean(form.dashboard_diagnostics_enabled)
  }
}

export function flattenBills(rows = []) {
  return rows.flatMap((bill) => [bill, ...flattenBills(bill.subbils || [])])
}

export function billCode(bill = {}) {
  return String(bill.name || bill.bill || bill.full_name || bill.display_name || '')
}

export function billOptions(rows = []) {
  return rows.map((bill) => ({
    label: `${billCode(bill) || bill.id} — ${bill.description || bill.full_name || bill.display_name || ''}`.trim(),
    value: bill.id
  }))
}

export function defaultBillId(rows, prefixes) {
  const bill = rows.find((row) => prefixes.some((prefix) => {
    const code = billCode(row)
    return code === prefix || code.startsWith(`${prefix}.`) || code.startsWith(`${prefix} `)
  }))
  return bill ? bill.id : null
}

export function inventoryAccountOptions(rows = []) {
  const ids = new Set(rows.filter((bill) => ['10', '41', '43'].some((code) => billCode(bill).startsWith(code))).map((bill) => bill.id))
  return billOptions(rows).filter((option) => ids.has(option.value))
}

export function applyOperationDefaults(form, kind, { warehouses, nomenclature, bills, settings }) {
  const defaultWarehouse = warehouses.find((row) => String(row.id) === String(settings.default_warehouse_id)) || warehouses.find((row) => row.code === 'MAIN') || warehouses[0]
  const defaultItem = nomenclature[0]
  const inventoryId = bills.some((row) => String(row.id) === String(settings.default_inventory_account_id))
    ? settings.default_inventory_account_id
    : defaultBillId(bills, ['41', '10', '43'])
  const itemId = defaultItem && (defaultItem.id || defaultItem.primary)
  const warehouseId = defaultWarehouse && defaultWarehouse.id
  if (!form.nomenclature_id) form.nomenclature_id = itemId || null
  if (kind === 'transfer') {
    if (!form.source_warehouse_id) form.source_warehouse_id = warehouseId || null
    if (!form.destination_warehouse_id || String(form.destination_warehouse_id) === String(form.source_warehouse_id)) {
      const destination = warehouses.find((row) => String(row.id) !== String(form.source_warehouse_id))
      form.destination_warehouse_id = destination ? destination.id : null
    }
  } else if (!form.warehouse_id) {
    form.warehouse_id = warehouseId || null
  }
  if (kind === 'opening') {
    if (!form.debit_account_id) form.debit_account_id = inventoryId
    if (!form.credit_account_id) form.credit_account_id = defaultBillId(bills, ['80', '83', '84'])
  } else {
    if (!form.inventory_account_id) form.inventory_account_id = inventoryId
    if (kind === 'writeOff' && !form.expense_account_id) {
      form.expense_account_id = settings.default_expense_account_id || defaultBillId(bills, ['44', '26', '20', '91'])
    }
  }
}

export function operationIsValid(kind, form) {
  const positive = (value) => Number(value) > 0
  if (kind === 'opening') return Boolean(form.date && form.warehouse_id && form.nomenclature_id && positive(form.count) && positive(form.amount) && form.debit_account_id)
  if (kind === 'transfer') return Boolean(form.date && form.source_warehouse_id && form.destination_warehouse_id && form.nomenclature_id && positive(form.count) && form.inventory_account_id && String(form.source_warehouse_id) !== String(form.destination_warehouse_id))
  if (kind === 'adjustment') return Boolean(form.date && form.warehouse_id && form.nomenclature_id && form.target_quantity !== '' && Number(form.target_quantity) >= 0 && form.inventory_account_id)
  return Boolean(form.date && form.warehouse_id && form.nomenclature_id && positive(form.count) && form.inventory_account_id)
}

export function operationPayload(kind, form, nomenclature = []) {
  const item = nomenclature.find((row) => String(row.id || row.primary) === String(form.nomenclature_id))
  const itemPayload = { nomenclature_id: form.nomenclature_id }
  if (item) itemPayload.position_name = item.common_name || item.name || item.code
  if (kind === 'opening') return { date: form.date, warehouse_id: form.warehouse_id, count: form.count, amount: form.amount, debit_account_id: form.debit_account_id, credit_account_id: form.credit_account_id, comment: form.comment, status: 'posted', ...itemPayload }
  if (kind === 'transfer') return { date: form.date, source_warehouse_id: form.source_warehouse_id, destination_warehouse_id: form.destination_warehouse_id, count: form.count, inventory_account_id: form.inventory_account_id, comment: form.comment, ...itemPayload }
  if (kind === 'adjustment') {
    const payload = { date: form.date, warehouse_id: form.warehouse_id, target_quantity: form.target_quantity, inventory_account_id: form.inventory_account_id, reason: form.reason, ...itemPayload }
    if (form.price !== '') payload.price = form.price
    return payload
  }
  return { date: form.date, warehouse_id: form.warehouse_id, count: form.count, inventory_account_id: form.inventory_account_id, expense_account_id: form.expense_account_id, reason: form.reason, ...itemPayload }
}

export function operationResult(response = {}) {
  const transfer = response.transfer || (Array.isArray(response.transfers) ? response.transfers[0] : null)
  return {
    documentId: response.document_id || (transfer && transfer.document_id) || null,
    transferId: transfer && transfer.id,
    delta: response.delta_quantity,
    amount: response.amount || (transfer && transfer.summ) || null
  }
}

export function workspaceError(response, fallback) {
  return warehouseError(response, fallback)
}

export function emptyWarehouseReports() {
  return {
    current_stock: { rows: [], summary: { quantity: '0', total_cost: '0.00' } },
    turnover: { rows: [], summary: { opening_quantity: '0', incoming_quantity: '0', outgoing_quantity: '0', closing_quantity: '0', incoming_cost: '0.00', outgoing_cost: '0.00' } },
    item_card: { opening_balance: '0', closing_balance: '0', movements: [] },
    low_zero_stock: { rows: [], summary: { items: 0, quantity: '0', total_cost: '0.00' }, threshold: '0' },
    valuation_by_warehouse: { rows: [], summary: { warehouses: 0, quantity: '0', total_cost: '0.00' } }
  }
}
